import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import TermsTemplatesList from "../../components/TermsTemplates/TermTemplatesList";
import LimitButton from "../../components/Commons/LimitSelectBox";
import Pagination from "../../components/Commons/TablePagination";
import useDebounce from "../../lib/useDebounce";
import { RootState } from "../../stores";
import {
  listTermTemplatesAction,
  setCurrentPageNumberAction,
  setDetailField,
  setFilterItemAction,
  setInitFilterItemAction,
  setPaginationItemAction,
} from "../../stores/termsTemplatesList";
import {
  TermTemplatesContainerProps,
  termTemplatesState,
} from "../../types/termTemplates";
import * as termTemplatesAPI from "../../api/termTemplates";
import TermTemplatesDeleteContainer from "./TermTemplatesDeleteContainer";
import TermTemplatesDetailContainer from "./TermTemplatesDetailContainer";
import TermTemplatesCreateContainer from "./TermTemplatesCreateContainer";

/* styled */
const FormCard = styled.article`
  width: 100%;
  position: relative;
`;

const TermsTemplatesListContainer = ({
  isOpen,
  termTemplatesView,
  handleViewChange,
}: TermTemplatesContainerProps) => {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const {
    termTemplatesTotal,
    termTemplatesItems,
    termTemplatesError,
    currentPageNumber,
    paginationItem,
    filterItem,
    userRole,
  } = useSelector(({ termTemplatesList, header }: RootState) => ({
    termTemplatesTotal: termTemplatesList.termTemplatesTotal,
    termTemplatesItems: termTemplatesList.termTemplatesItems,
    termTemplatesError: termTemplatesList.termTemplatesError,
    currentPageNumber: termTemplatesList.currentPageNumber,
    paginationItem: termTemplatesList.paginationItem,
    filterItem: termTemplatesList.filterItem,
    userRole: header.userRole,
  }));
  const [isDebounce, setIsDebounce] = useState<boolean>(false); //디바운싱 사용 구분을 위해 추가

  const [isOpenTermTemplatesDeleteModal, setIsOpenTermTemplatesDeleteModal] =
    useState<boolean>(false);
  const [selectedTermTemplatesId, setSelectedTermTemplatesId] =
    useState<number>(0);
  const [selectedTermTemplatesName, setSelectedTermTemplatesName] =
    useState<string>("");

  const handleListTermTemplatesList = useCallback(() => {
    dispatch(listTermTemplatesAction({ ...filterItem, ...paginationItem }));
  }, [dispatch, filterItem, paginationItem]);

  //필터 핸들링
  const handleFilter = useCallback(
    (targetItem: any, isDebounce: boolean) => {
      setIsDebounce(isDebounce); //디바운싱 처리를 위해 하위 Component에서 구분을 받아옴
      dispatch(setFilterItemAction({ ...filterItem, ...targetItem }));
    },
    [dispatch, filterItem],
  );

  //필터 초기화 버튼
  const handleinitFilter = useCallback(() => {
    dispatch(setInitFilterItemAction());
  }, [dispatch]);

  const handleTermTemplatesCreateView = () => {
    handleViewChange("termsTemplates", "create");
  };

  //상세화면 이동용
  const handleTermTemplatesDetail = useCallback(
    (id: number) => {
      handleViewChange("termsTemplates", "detail");
      dispatch(setDetailField(id));
    },
    [handleViewChange, dispatch],
  );

  const handleTermTemplatesDeleteModal = useCallback(
    (id: number, room_no: string) => {
      setSelectedTermTemplatesId(id);
      setSelectedTermTemplatesName(room_no);
      setIsOpenTermTemplatesDeleteModal(true);
    },
    [],
  );

  //현재 페이지 전역상태 등록
  const handleCurrentPageNumber = useCallback(
    (currentPageNumber: number) => {
      dispatch(setCurrentPageNumberAction({ currentPageNumber }));
    },
    [dispatch],
  );

  //페이지 전역상태 등록
  const handlePaginationItem = useCallback(
    (paginationItem: termTemplatesState["paginationItem"]) => {
      dispatch(setPaginationItemAction({ paginationItem }));
    },
    [dispatch],
  );

  //페이지네이션 이동
  const changePagination = useCallback(
    (pageNumber: number) => {
      handleCurrentPageNumber(pageNumber);
      handlePaginationItem({
        ...paginationItem,
        offset: (pageNumber - 1) * paginationItem.limit,
      });
    },
    [handleCurrentPageNumber, handlePaginationItem, paginationItem],
  );

  //목록 리미트
  const changeLimit = useCallback(
    (limit: number) => {
      handleCurrentPageNumber(1);
      handlePaginationItem({
        offset: 0,
        limit,
      });
    },
    [handleCurrentPageNumber, handlePaginationItem],
  );

  //디바운싱 훅으로 이동
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebounce = useCallback(
    useDebounce((handleListTermTemplates) => handleListTermTemplates(), 300), //0.3초 동안 미입력 시 함수 실행
    [],
  );

  const handleToggle = useCallback(
    async (id: number, currentStatus: boolean) => {
      try {
        const nextStatus = currentStatus;

        await termTemplatesAPI.updateTermTemplate({
          id,
          is_published: nextStatus,
        });

        handleListTermTemplatesList();
      } catch (e) {
        console.error(e);
        alert("상태 변경에 실패했습니다.");
      }
    },
    [handleListTermTemplatesList],
  );

  useEffect(() => {
    try {
      if (isOpen)
        !isDebounce
          ? handleListTermTemplatesList()
          : handleDebounce(handleListTermTemplatesList); //디바운싱 처리
    } catch (error) {
      throw error;
    }
  }, [isOpen, handleListTermTemplatesList, isDebounce, handleDebounce]);

  useEffect(() => {
    if (termTemplatesError) {
      if (
        termTemplatesError.response.data?.code === 401 ||
        termTemplatesError.response.data?.code === 419
      ) {
        localStorage.clear();
        navigation("/login");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termTemplatesError]);

  return (
    <>
      {termTemplatesView === "list" && (
        <>
          <FormCard hidden={termTemplatesView !== "list"}>
            <TermsTemplatesList
              termTemplatesItems={termTemplatesItems}
              filterItem={filterItem}
              userRole={userRole}
              termTemplatesTotal={termTemplatesTotal}
              handleToggle={handleToggle}
              handleFilter={handleFilter}
              handleinitFilter={handleinitFilter}
              handleTermTemplatesDetail={handleTermTemplatesDetail}
              handleTermTemplatesCreateModal={handleTermTemplatesCreateView}
              handleTermTemplatesDeleteModal={handleTermTemplatesDeleteModal}
            />
            <LimitButton
              currentLimit={paginationItem.limit}
              changeLimit={changeLimit}
            />
            <Pagination
              total={termTemplatesTotal}
              index={currentPageNumber}
              limit={paginationItem.limit}
              indexChange={changePagination}
            />
          </FormCard>

          <TermTemplatesDeleteContainer
            isOpen={isOpenTermTemplatesDeleteModal}
            toggle={() =>
              setIsOpenTermTemplatesDeleteModal(!isOpenTermTemplatesDeleteModal)
            }
            reload={() => handleListTermTemplatesList()}
            id={selectedTermTemplatesId}
            termTemplatesListName={selectedTermTemplatesName}
          />
        </>
      )}
      {termTemplatesView === "detail" && (
        <TermTemplatesDetailContainer
          isOpen={termTemplatesView === "detail"}
          listTermTemplates={handleListTermTemplatesList}
          handleViewChange={handleViewChange}
        />
      )}

      {termTemplatesView === "create" && (
        <TermTemplatesCreateContainer
          isOpen={termTemplatesView === "create"}
          listTermTemplates={handleListTermTemplatesList}
          handleViewChange={handleViewChange}
        />
      )}
    </>
  );
};

export default TermsTemplatesListContainer;
